<template>
  <basic-page title="Kululaskut">
    <v-row class="fill-height">
      <v-col cols="12" lg="8" offset-lg="2" xl="8" offset-xl="2">
        <v-card class="mb-4">
          <v-toolbar flat v-if="$isTreasurer">
            <v-tabs v-model="activeTab" @change="fetchExpenseClaims">
              <v-tab>Omat</v-tab>
              <v-tab>
                Muut
                <v-badge
                  v-if="pendingCount > 0"
                  color="red"
                  :content="pendingCount"
                  inline
                  class="ml-2"
                ></v-badge>
              </v-tab>
            </v-tabs>
          </v-toolbar>

          <v-card-title>
            <span v-if="!$isTreasurer || activeTab === 0">Omat kululaskut</span>
            <span v-else>Muiden kululaskut</span>
            <v-spacer></v-spacer>

            <v-select
              v-if="$isTreasurer && activeTab === 1"
              v-model="statusFilter"
              :items="statusOptions"
              label="Suodata tila"
              dense
              outlined
              hide-details
              class="mr-4"
              style="max-width: 200px"
              @change="fetchExpenseClaims"
            ></v-select>

            <v-btn color="primary" to="/kululaskut/uusi">
              <v-icon left>mdi-plus</v-icon>
              Uusi kululasku
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="expenseClaims"
              :loading="loading"
              no-data-text="Ei kululaskuja"
            >
              <template v-slot:item.status="{ item }">
                <v-chip :color="getStatusColor(item.status)" small>
                  {{ getStatusText(item.status) }}
                </v-chip>
              </template>
              <template v-slot:item.submissionDate="{ item }">
                {{ formatDate(item.submissionDate) }}
              </template>
              <template v-slot:item.total="{ item }">
                {{ formatCurrency(item.totals.grandTotal) }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon small @click="downloadPdf(item)" title="Lataa PDF">
                  <v-icon>mdi-file-pdf-box</v-icon>
                </v-btn>

                <v-btn
                  icon
                  small
                  @click="editClaim(item)"
                  title="Muokkaa"
                  v-if="canEdit(item)"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>

                <v-menu offset-y v-if="$isTreasurer">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      title="Muuta tilaa"
                    >
                      <v-icon>mdi-gavel</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="updateStatus(item, 'Approved')">
                      <v-list-item-title>Hyväksy</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="updateStatus(item, 'Paid')">
                      <v-list-item-title>Maksettu</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="updateStatus(item, 'Pending')">
                      <v-list-item-title>Palauta käsittelyyn</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="updateStatus(item, 'Rejected')">
                      <v-list-item-title>Hylkää</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import dayjs from "dayjs";
import { firestore as db } from "@/plugins/firebase.app";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";
import { orderBy } from "lodash";
import { generateClaimPDF } from "@/utils/pdfGenerator";

export default {
  name: "Kululaskut",
  components: { BasicPage },
  data() {
    return {
      expenseClaims: [],
      loading: false,
      activeTab: 0,
      pendingCount: 0,
      statusFilter: "All",
      statusOptions: [
        { text: "Kaikki", value: "All" },
        { text: "Avoin", value: "Pending" },
        { text: "Hyväksytty", value: "Approved" },
        { text: "Maksettu", value: "Paid" },
        { text: "Hylätty", value: "Rejected" },
      ],
      headers: [
        { text: "Pvm", value: "submissionDate" },
        { text: "Hakija", value: "memberName" },
        { text: "Matkan syy", value: "tripReason" },
        { text: "Tila", value: "status" },
        { text: "Summa", value: "total" },
        { text: "Toiminnot", value: "actions", sortable: false },
      ],
    };
  },
  computed: {},
  created() {
    this.fetchExpenseClaims();
  },
  methods: {
    async fetchExpenseClaims() {
      this.loading = true;
      try {
        const claimsRef = collection(db, "expense_claims");
        let q;

        if (this.$isTreasurer) {
          // Treasurer logic: Fetch all to calculate pending counts
          q = query(claimsRef);
        } else {
          // Regular users see only their own claims
          q = query(claimsRef, where("memberId", "==", this.$user.uid));
        }

        const querySnapshot = await getDocs(q);
        const allClaims = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Calculate pending count for "Muut" (others' pending claims)
        if (this.$isTreasurer) {
          this.pendingCount = allClaims.filter(
            (c) => c.memberId !== this.$user.uid && c.status === "Pending"
          ).length;
        }

        let claims = allClaims;

        // Filter for Treasurer Tabs
        if (this.$isTreasurer) {
          if (this.activeTab === 0) {
            claims = claims.filter((c) => c.memberId === this.$user.uid);
          } else {
            claims = claims.filter((c) => c.memberId !== this.$user.uid);
          }

          // Apply Status Filter
          if (this.statusFilter !== "All") {
            claims = claims.filter((c) => c.status === this.statusFilter);
          }
        }

        // Sort by submission date desc
        this.expenseClaims = orderBy(claims, ["submissionDate"], ["desc"]);
      } catch (e) {
        console.error("Failed to fetch expense claims", e);
        this.$toast.error("Virhe haettaessa kululaskuja");
      } finally {
        this.loading = false;
      }
    },
    canEdit(item) {
      // User can edit if status is not Approved/Paid/Rejected (i.e. Pending)
      // OR if user is Treasurer (maybe treasurer can edit anything? Or just status?)
      // User said: "user should be able to edit their kululasku but after marking it 'accepted' ... they cannot edit it anymore."
      // Treasurer can return it to open.
      // So:
      // 1. If I am the owner, I can edit if status is 'Pending' (or undefined).
      // 2. If I am Treasurer, I can probably edit anything, or at least I can change status.
      // Let's restrict editing content to Owner when Pending.
      // Treasurer might need to edit content too? "Rahastonhoitaja can return... to be open".
      // Let's assume only Owner edits content, but only when Pending.

      const isOwner = item.memberId === this.$user.uid;
      const isEditableStatus = item.status === "Pending" || !item.status;

      if (isOwner && isEditableStatus) return true;

      // Treasurer can edit? Maybe not content, just status.
      // If treasurer wants to edit content, they should probably return it to pending and let user fix it,
      // or maybe they can edit. Let's allow Treasurer to edit content for now if they want.
      if (this.$isTreasurer) return true;

      return false;
    },
    editClaim(item) {
      this.$router.push({ name: "ExpenseClaimEdit", params: { id: item.id } });
    },
    async downloadPdf(item) {
      try {
        const doc = await generateClaimPDF(item);
        const safeName = (item.memberName || "user")
          .replace(/[^a-z0-9]/gi, "_")
          .toLowerCase();
        doc.save(
          `matkalasku_${safeName}_${dayjs(item.submissionDate).format(
            "YYYY-MM-DD"
          )}.pdf`
        );
        this.$toast.success("PDF ladattu");
      } catch (e) {
        console.error("PDF generation failed", e);
        this.$toast.error("PDF luonti epäonnistui");
      }
    },
    async updateStatus(item, status) {
      try {
        const oldStatus = item.status;
        const docRef = doc(db, "expense_claims", item.id);
        await updateDoc(docRef, { status });
        item.status = status;

        // Update pending count if needed
        if (this.$isTreasurer && item.memberId !== this.$user.uid) {
          if (oldStatus === "Pending" && status !== "Pending") {
            this.pendingCount--;
          } else if (oldStatus !== "Pending" && status === "Pending") {
            this.pendingCount++;
          }
        }

        this.$toast.success(`Tila päivitetty: ${status}`);
      } catch (e) {
        console.error("Status update failed", e);
        this.$toast.error("Tilan päivitys epäonnistui");
      }
    },
    formatDate(date) {
      return dayjs(date).format("DD.MM.YYYY");
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("fi-FI", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    },
    getStatusText(status) {
      const map = {
        Pending: "Avoin",
        Approved: "Hyväksytty",
        Paid: "Maksettu",
        Rejected: "Hylätty",
      };
      return map[status] || status;
    },
    getStatusColor(status) {
      switch (status) {
        case "Approved":
        case "Paid":
          return "success";
        case "Rejected":
          return "error";
        case "Pending":
          return "warning";
        default:
          return "grey";
      }
    },
  },
};
</script>
